import React, { useState } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { MapPin, Home as HomeIcon, Phone, MessageCircle, Search, Filter, ArrowRight } from 'lucide-react';
import { allProperties, companyInfo } from '../mock';

// Import local videos
import imtVideo from '../assets/imtplot.mp4';
import sohnaVideo from '../assets/sohnaplot.mp4';
import gurgaonVideo from '../assets/gurgaonplot.mp4';
import ataVideo from '../assets/sohnaGurgaonATA.mp4';
import sector59Video from '../assets/sector59.mp4';
import baharampurVideo from '../assets/baharampur.mp4';
import salapurVideo from '../assets/salapur.mp4';
import delhiVideo from '../assets/delhi.mp4';
import marutiKunjVideo from '../assets/marutiKunj.mp4';

// Map each plot title to its local video
const videoMap = {
  'IMT Plot': imtVideo,
  'Sohna Plot': sohnaVideo,
  'Gurgaon Plot': gurgaonVideo,
  'Sohna–Gurgaon Road (ATA Area) Plot': ataVideo,
  'Sector 59 Plot': sector59Video,
  'Baharampur Plot': baharampurVideo,
  'Salapur Plot': salapurVideo,
  'Delhi Plot': delhiVideo,
  'Maruti Kunj Plot': marutiKunjVideo,
};

const Properties = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [priceRange, setPriceRange] = useState('all');
  const [videoOpen, setVideoOpen] = useState({}); // track which property shows video

  const handleWhatsApp = (property) => {
    const message = `Hi, I'm interested in ${property.title} - ${property.price}`;
    window.open(`https://wa.me/91${companyInfo.whatsapp}?text=${encodeURIComponent(message)}`, '_blank');
  };

  const handleCall = () => {
    window.location.href = `tel:+91${companyInfo.phones[0]}`;
  };

  const toggleVideo = (id) => {
    setVideoOpen((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const filteredProperties = allProperties.filter(property => {
    const matchesSearch = property.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         property.location.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'all' || property.type === filterType;
    
    let matchesPrice = true;
    if (priceRange !== 'all') {
      const price = parseInt(property.price.replace(/[^0-9]/g, ''));
      if (priceRange === 'low') matchesPrice = price < 3000000;
      else if (priceRange === 'medium') matchesPrice = price >= 3000000 && price < 6000000;
      else if (priceRange === 'high') matchesPrice = price >= 6000000;
    }

    return matchesSearch && matchesType && matchesPrice;
  });

  return (
    <div className="properties-page min-h-screen bg-black pt-24 pb-20">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-12">
          <Badge className="mb-4 bg-orange-500/20 text-orange-400 border-orange-500/50">
            All Properties
          </Badge>
          <h1 className="text-5xl md:text-6xl font-bold mb-4">
            <span className="gradient-text">Explore Our Portfolio</span>
          </h1>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            Find your perfect property from our extensive collection of plots and homes
          </p>
        </div>

        {/* Search and Filter */}
        <Card className="filter-card p-6 mb-12">
          <div className="grid md:grid-cols-4 gap-4">
            <div className="md:col-span-2 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 w-5 h-5" />
              <Input
                type="text"
                placeholder="Search by location or title..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-gray-900 border-gray-700 text-white"
              />
            </div>

            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="bg-gray-900 border-gray-700 text-white">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Property Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="plot">Plots</SelectItem>
                <SelectItem value="home">Homes</SelectItem>
              </SelectContent>
            </Select>

            <Select value={priceRange} onValueChange={setPriceRange}>
              <SelectTrigger className="bg-gray-900 border-gray-700 text-white">
                <SelectValue placeholder="Price Range" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Prices</SelectItem>
                <SelectItem value="low">Under ₹30L</SelectItem>
                <SelectItem value="medium">₹30L - ₹60L</SelectItem>
                <SelectItem value="high">Above ₹60L</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </Card>

        {/* Properties Grid */}
        {filteredProperties.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredProperties.map((property) => {
              const isVideo = videoOpen[property.id] && property.type === 'plot' && videoMap[property.title];
              return (
                <Card key={property.id} className="property-card group">
                  <div className="property-image-wrapper relative overflow-hidden">
                    {/* Show either image or video */}
                    {!isVideo ? (
                      <img
                        src={property.image}
                        alt={property.title}
                        className="property-image w-full h-64 object-cover"
                      />
                    ) : (
                      <video
                        src={videoMap[property.title]}
                        className="property-image w-full h-64 object-cover"
                        autoPlay
                        muted
                        loop
                        playsInline
                      />
                    )}

                    {/* Badges */}
                    <div className="absolute top-4 right-4">
                      <Badge className="bg-orange-500 text-white border-0">{property.status}</Badge>
                    </div>
                    <div className="absolute top-4 left-4">
                      <Badge className="bg-black/80 text-orange-400 border-orange-500/50">
                        {property.type === 'plot' ? (
                          <><MapPin className="w-3 h-3 mr-1" /> Plot</>
                        ) : (
                          <><HomeIcon className="w-3 h-3 mr-1" /> Home</>
                        )}
                      </Badge>
                    </div>

                    {/* Arrow toggle */}
                    {property.type === 'plot' && videoMap[property.title] && (
                      <Button
                        size="sm"
                        className="absolute bottom-4 right-4 bg-black/50 text-white hover:bg-black/70"
                        onClick={() => toggleVideo(property.id)}
                      >
                        {isVideo ? '📷' : '▶️'}
                      </Button>
                    )}
                  </div>

                  {/* Property details */}
                  <div className="p-6">
                    <h3 className="text-xl font-bold mb-2 text-white group-hover:text-orange-400 transition-colors">
                      {property.title}
                    </h3>
                    <div className="flex items-center text-gray-400 text-sm mb-4">
                      <MapPin className="w-4 h-4 mr-1" />
                      {property.location}
                    </div>

                    {property.type === 'plot' ? (
                      <div className="grid grid-cols-2 gap-3 mb-4">
                        <div>
                          <div className="text-xs text-gray-500">Size</div>
                          <div className="text-sm font-semibold text-white">{property.size}</div>
                        </div>
                        <div>
                          <div className="text-xs text-gray-500">Dimensions</div>
                          <div className="text-sm font-semibold text-white">{property.dimensions}</div>
                        </div>
                        <div className="col-span-2">
                          <div className="text-xs text-gray-500">Road Width</div>
                          <div className="text-sm font-semibold text-orange-400">{property.roadWidth}</div>
                        </div>
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 gap-3 mb-4">
                        <div>
                          <div className="text-xs text-gray-500">Bedrooms</div>
                          <div className="text-sm font-semibold text-white">{property.bedrooms} BHK</div>
                        </div>
                        <div>
                          <div className="text-xs text-gray-500">Area</div>
                          <div className="text-sm font-semibold text-white">{property.size}</div>
                        </div>
                      </div>
                    )}

                    <div className="mb-4">
                      <div className="text-xs text-gray-500 mb-2">Features</div>
                      <div className="flex flex-wrap gap-2">
                        {property.features.slice(0, 3).map((feature, idx) => (
                          <Badge key={idx} variant="outline" className="text-xs border-orange-500/50 text-orange-400">
                            {feature}
                          </Badge>
                        ))}
                      </div>
                    </div>

                    <div className="pt-4 border-t border-gray-800">
                      <div className="flex items-center justify-between mb-4">
                        <div className="text-2xl font-bold gradient-text">{property.price}</div>
                      </div>

                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          className="flex-1 cta-button-primary"
                          onClick={() => handleWhatsApp(property)}
                        >
                          <MessageCircle className="w-4 h-4 mr-2" />
                          WhatsApp
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="cta-button-secondary"
                          onClick={handleCall}
                        >
                          <Phone className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        ) : (
          <Card className="p-12 text-center">
            <div className="text-gray-500 mb-4">
              <Search className="w-16 h-16 mx-auto mb-4 opacity-50" />
              <h3 className="text-xl font-semibold text-white mb-2">No Properties Found</h3>
              <p>Try adjusting your search or filters</p>
            </div>
            <Button
              onClick={() => {
                setSearchTerm('');
                setFilterType('all');
                setPriceRange('all');
              }}
              className="cta-button-primary mt-4"
            >
              Clear All Filters
            </Button>
          </Card>
        )}

        {/* Contact CTA */}
        <div className="mt-16 text-center">
          <Card className="cta-card p-12">
            <h2 className="text-3xl font-bold mb-4 text-white">
              Can't Find What You're Looking For?
            </h2>
            <p className="text-gray-400 mb-8 text-lg">
              Contact us and we'll help you find the perfect property that matches your requirements
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Button
                size="lg"
                className="cta-button-primary group"
                onClick={() => handleWhatsApp({ title: 'Custom Property', price: '' })}
              >
                <MessageCircle className="mr-2 w-5 h-5" />
                Chat on WhatsApp
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="cta-button-secondary"
                onClick={handleCall}
              >
                <Phone className="mr-2 w-5 h-5" />
                Call Now
              </Button>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Properties;